import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './CardsList.styles';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { sleep } from '@/utils/helpers';
import { CardsItem } from '@/components/Profile/Cards/CardsItem';
import { CardSettings } from '@/components/Profile/Cards/CardSettings';
import {
  CardDataStat,
  MAX_PAYMENT_CARDS,
} from '@/components/Profile/Cards/Cards.constants';
import { SavedCard } from '@/api/types/cards.types';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Dropdown } from '@/features/BookmarkCollections/components/Dropdown';

interface CardsListProps {
  cards: SavedCard[];
  onAddCard: () => void;
  onRenameCard: (id: number, name: string, onSuccess?: () => void) => void;
  onDeleteCard: (id: number, onSuccess?: () => void) => void;
  onMakePrimary: (id: number, onSuccess?: () => void) => void;
}

export const CardsList: React.FC<CardsListProps> = ({
  cards,
  onAddCard,
  onRenameCard,
  onDeleteCard,
  onMakePrimary,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const [selectedCard, setSelectedCard] = useState<SavedCard | null>(null);
  const [isOpen, toggle] = useToggleOpenState();
  const [selectedAction, setSelectedAction] = useState<
    'menu' | 'rename' | 'remove'
  >('menu');
  const [openedDropdownCardId, setOpenedDropdownCardId] = useState<
    number | null
  >(null);
  const canAddCard = cards.length < MAX_PAYMENT_CARDS;

  useEffect(() => {
    if (!selectedCard) return;

    const updatedCard = cards.find(card => card.id === selectedCard.id);
    if (updatedCard === selectedCard) return;

    if (!updatedCard) {
      setSelectedCard(null);
      toggle(false);
      return;
    }

    setSelectedCard(updatedCard);
  }, [cards, selectedCard, toggle]);

  async function openCardMenu(card: SavedCard) {
    setSelectedCard(card);
    if (isDesktop) {
      setOpenedDropdownCardId(prev => (prev === card.id ? null : card.id));
      return;
    }

    setSelectedAction('menu');
    await sleep(0);
    toggle(true);
  }

  function closeCardDropdown() {
    setOpenedDropdownCardId(null);
  }

  function onMakePrimaryClick(card: SavedCard) {
    onMakePrimary(card.id);
    closeCardDropdown();
  }

  function onRenameClick(card: SavedCard) {
    setSelectedCard(card);
    setSelectedAction('rename');
    closeCardDropdown();
  }

  function onRemoveClick(card: SavedCard) {
    setSelectedCard(card);
    setSelectedAction('remove');
    closeCardDropdown();
  }

  function resetActionState() {
    setSelectedAction('menu');
  }

  return (
    <>
      <S.Section>
        <S.Header>
          <S.Title $variant="2">{t('pages.cabinet.my_cards')}</S.Title>
          {canAddCard && (
            <S.AddCardLink
              onClick={onAddCard}
              data-stat={CardDataStat.AddNewCard}
            >
              {t('pages.cabinet.add_new_card')}
            </S.AddCardLink>
          )}
        </S.Header>

        <S.Grid>
          {cards.map(card => (
            <CardsItem
              key={card.id}
              card={card}
              onMoreClick={openCardMenu}
              dropdown={
                isDesktop ? (
                  <Dropdown
                    isOpen={openedDropdownCardId === card.id}
                    onClose={closeCardDropdown}
                  >
                    <S.DropdownMenu>
                      {!card.isPrimary && card.isActive && !card.isExpired && (
                        <S.DropdownMenuItem
                          onClick={() => onMakePrimaryClick(card)}
                          data-stat={CardDataStat.SetPrimaryCard}
                        >
                          <S.DropdownMenuItemText>
                            {t('pages.cabinet.make_main')}
                          </S.DropdownMenuItemText>
                        </S.DropdownMenuItem>
                      )}
                      {card.isActive && !card.isExpired && (
                        <S.DropdownMenuItem
                          onClick={() => onRenameClick(card)}
                          data-stat={CardDataStat.RenameCard}
                        >
                          <S.DropdownMenuItemText>
                            {t('pages.cabinet.rename_card')}
                          </S.DropdownMenuItemText>
                        </S.DropdownMenuItem>
                      )}
                      <S.DropdownMenuItem
                        onClick={() => onRemoveClick(card)}
                        data-stat={CardDataStat.RemoveCard}
                      >
                        <S.DropdownMenuItemText $isDangerous>
                          {t('pages.cabinet.remove_card')}
                        </S.DropdownMenuItemText>
                      </S.DropdownMenuItem>
                    </S.DropdownMenu>
                  </Dropdown>
                ) : null
              }
            />
          ))}
        </S.Grid>
      </S.Section>

      {selectedCard &&
        (isDesktop ? isOpen || selectedAction !== 'menu' : true) && (
          <CardSettings
            isOpen={isOpen}
            close={() => {
              toggle(false);
              resetActionState();
            }}
            card={selectedCard}
            onRename={(name, onSuccess) =>
              onRenameCard(selectedCard.id, name, () => {
                onSuccess?.();
                resetActionState();
              })
            }
            onDelete={onSuccess =>
              onDeleteCard(selectedCard.id, () => {
                onSuccess?.();
                resetActionState();
              })
            }
            onMakePrimary={onSuccess =>
              onMakePrimary(selectedCard.id, () => {
                onSuccess?.();
                resetActionState();
              })
            }
            selectedAction={selectedAction}
            onActionStateReset={resetActionState}
          />
        )}
    </>
  );
};
